import { useEffect, useId, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { CachedPrefixAchievement, getPrefixAchievements } from 'services/home-feed.service';
import { GetTaskLevelLabel } from 'utils/strings';

import { initials, PersonAvatar, rostoDoAutor } from 'components/person-avatar';
import { ROTA_DAS_REALIZACOES_DO_PREFIXO } from 'pages/Rede/rotas-do-feed';
import { formatName } from './recent-recognitions.format';
import { ArrowLink, SectionHeading } from './section-heading';

const MAX_CARDS = 3;

// Cada cartão numa cor e inclinação, como adesivos na parede.
const CARD_STYLES = [
  'bg-brand-cyan-light -rotate-[1.8deg]',
  'bg-brand-purple-light rotate-[1.2deg]',
  'bg-brand-yellow-light -rotate-[0.8deg]',
];

/**
 * Lê as realizações do prefixo do cache que o scheduler recalcula de 2 em 2 horas, uma vez por prefixo e
 * ciclo (FEED-14). Falha ou vazio viram lista vazia.
 */
export function usePrefixAchievements(prefixo?: number, cycleId?: string) {
  const [items, setItems] = useState<CachedPrefixAchievement[]>([]);

  useEffect(() => {
    if (!prefixo || !cycleId) return;

    let cancelled = false;

    getPrefixAchievements(prefixo, cycleId)
      .then(cached => {
        if (!cancelled) setItems(cached.slice(0, MAX_CARDS));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, [prefixo, cycleId]);

  return items;
}

interface PrefixAchievementsProps {
  prefixo?: number;
  cycleId?: string;
}

/** "Últimas realizações destaque": as de maior pontuação da turma no ciclo, lidas do cache (FEED-14). */
export function PrefixAchievements({ prefixo, cycleId }: PrefixAchievementsProps) {
  const items = usePrefixAchievements(prefixo, cycleId);
  const headingId = useId();

  if (!items.length) return null;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <SectionHeading id={headingId}>
          Últimas realizações destaque
          {prefixo && <span className="text-jj-body-sm text-content"> • no prefixo {prefixo}</span>}
        </SectionHeading>

        <ArrowLink to={ROTA_DAS_REALIZACOES_DO_PREFIXO}>Ver todos no Feed Joga Junto</ArrowLink>
      </div>

      {/* D3: 18px e 5px não têm degrau no Tailwind, nem há rounded de 18px. */}
      <ul className="grid gap-[18px] pb-2 pt-1 sm:grid-cols-3">
        {items.map((item, index) => {
          const created = createdLabel(item.created);
          const author = item.author ? formatName(item.author) : '';

          return (
            <li key={item._id}>
              <article
                className={`flex h-full flex-col gap-2.5 rounded-[18px] px-5 pb-5 pt-[18px] text-content shadow-jj-tile duration-200 ease-jj-standard transition-[transform,box-shadow] hover:-translate-y-[5px] hover:shadow-jj-tile-hover ${CARD_STYLES[index % CARD_STYLES.length]}`}
              >
                {created && <p className="text-jj-label leading-normal text-brand-blue-dark">{created}</p>}

                <h3 className="font-display text-jj-body font-bold leading-jj-h1 text-brand-blue-dark">{item.titulo}</h3>

                <span aria-hidden="true" className="mb-0.5 mt-auto h-px bg-brand-white" />

                <div className="flex items-center gap-2.5">
                  {author && (
                    // Rosto do dono: foto ou avatar montado; sem eles, as iniciais (AJH-52).
                    <PersonAvatar
                      {...rostoDoAutor(item)}
                      initials={initials(author)}
                      size={32}
                      initialsClassName="bg-brand-white font-display text-jj-label font-bold text-content"
                    />
                  )}
                  <p className="min-w-0 text-jj-label leading-jj-caption">
                    {author && <b className="font-display">{author}</b>}
                    {author && !!item.participantes && (
                      <span className="text-jj-micro">
                        {' '}
                        · + {item.participantes} {item.participantes === 1 ? 'participante' : 'participantes'}
                      </span>
                    )}
                    {author && <br />}
                    {/* `brand-blue-dark`, não o `brand-blue` do design: sobre o ciano e o lilás dos cartões o azul
                        claro fica entre 2,6 e 3:1. */}
                    <b className="font-semibold text-brand-blue-dark">{GetTaskLevelLabel(item.status || '')}</b>
                  </p>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** "12 jun": a data de cadastro no formato do cartão; sem data válida, nada (HOME-31). */
function createdLabel(created?: number | string) {
  if (created === undefined || created === null || created === '') return '';

  const date = new Date(created);
  return isNaN(date.getTime()) ? '' : format(date, 'dd MMM', { locale: ptBR }).replace('.', '');
}
